import {
  createSlice,
  createEntityAdapter,
  createSelector,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { HYDRATE } from "next-redux-wrapper";
import { decamelizeKeys, camelize } from "humps";
import { stringify } from "qs";
import { createWrapper } from "admin/api/axiosWrapper";
import { ErrorResponse } from "admin/types/errorResponse";
import { Booking } from "admin/types/booking";
import {
  isFulfilledAction,
  isPendingAction,
  isRejectedAction,
} from "admin/utils/sliceUtil";
import {
  API_BASE_PATH,
  COMPANY_ADMIN_API_VER,
} from "admin/constants/constants";
import { RootState } from "admin/store";
import { PageContext } from "admin/types/pageContext";
import { AdminTypes } from "admin/constants/adminTypes";
import { Meta } from "admin/types/meta";
import { HotelChildrenSettingAllCategories } from "admin/constants/hotelChildrenSettingCategories";
import { BookingSortTypes } from "admin/constants/bookingSortTypes";
import { SortOrderTypes } from "admin/constants/sortOrderTypes";

const key = "booking";
const adapter = createEntityAdapter<Booking.Booking, string>({
  selectId: (booking) => booking.code,
});
const managerBookingAdapter = createEntityAdapter<Booking.Booking, string>({
  selectId: (booking) => booking.code,
});

const managerBookingInitialStateValues: Booking.ManagerBookingState = {
  params: {},
  meta: null,
};
const managerBookingInitialState = managerBookingAdapter.getInitialState(
  managerBookingInitialStateValues,
);

const initialStateValues: Booking.BookingState = {
  bookingId: null,
  error: null,
  loading: false,
  params: {},
  meta: null,
  isSortUpdated: false,
  sortName: BookingSortTypes.createdDate,
  sortOrder: SortOrderTypes.desc,
  managerBookings: managerBookingInitialState,
};
const initialState = adapter.getInitialState(initialStateValues);

/** Async * */
export const fetchManagerBookings = createAsyncThunk<
  {
    bookings: Booking.Booking[];
    meta: Meta;
  },
  {
    param?: string;
    headers?: any;
    isServerSide?: boolean;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchManagerBookings`, async (args, { rejectWithValue }) => {
  try {
    const { param, headers, isServerSide, locale } = args;
    const url = `${API_BASE_PATH}/manager/bookings.json?${param}`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      bookings: result.data.bookings,
      meta: result.data.meta,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const fetchBookings = createAsyncThunk<
  {
    bookings: Booking.Booking[];
    meta: Meta;
  },
  {
    context: PageContext.PageContext;
    hotelId: string;
    param?: string;
    headers?: any;
    isServerSide?: boolean;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchBookings`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelId, param, headers, isServerSide, locale } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/bookings.json?${param}`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/bookings.json?${param}`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      bookings: result.data.bookings,
      meta: result.data.meta,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const fetchBooking = createAsyncThunk<
  {
    booking: Booking.Booking;
  },
  {
    context: PageContext.PageContext;
    id: string;
    headers?: any;
    isServerSide?: boolean;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchBooking`, async (args, { rejectWithValue }) => {
  try {
    const { context, id, headers, isServerSide, locale } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/bookings/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/bookings/${id}.json`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      booking: result.data.booking,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const cancelBooking = createAsyncThunk<
  {
    booking: Booking.Booking;
  },
  {
    context: PageContext.PageContext;
    id: string;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/cancelBooking`, async (args, { rejectWithValue }) => {
  try {
    const { context, id, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/bookings/${id}/cancellations.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/bookings/${id}/cancellations.json`;
    const result = await createWrapper().post(url, { cancellation: params });
    return {
      booking: result.data.booking,
    };
  } catch (err) {
    console.log(err.data.errors);
    return rejectWithValue(err.data.errors);
  }
});

export const confirmBooking = createAsyncThunk<
  {
    booking: Booking.Booking;
  },
  {
    context: PageContext.PageContext;
    id: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/confirmBooking`, async (args, { rejectWithValue }) => {
  try {
    const { context, id } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/bookings/${id}/confirmation.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/bookings/${id}/confirmation.json`;
    const result = await createWrapper().post(url);
    return {
      booking: result.data.booking,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const cancelBookingRoom = createAsyncThunk<
  {
    booking: Booking.Booking;
  },
  {
    context: PageContext.PageContext;
    id: string;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/cancelBookingRoom`, async (args, { rejectWithValue }) => {
  try {
    const { context, id, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/booking_rooms/${id}/cancellations.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/booking_rooms/${id}/cancellations.json`;
    const result = await createWrapper().post(url, { cancellation: params });
    return {
      booking: result.data.booking,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const confirmBookingRoomGuests = createAsyncThunk<
  {
    booking: Booking.Booking;
  },
  {
    context: PageContext.PageContext;
    bookingRoomId: string;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/confirmBookingRoomGuests`, async (args, { rejectWithValue }) => {
  try {
    const { context, bookingRoomId, params } = args;
    const paramString = stringify(
      decamelizeKeys({
        guest_cancellation_confirmation: params,
      }),
      {
        arrayFormat: "brackets",
      },
    );
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/booking_rooms/${bookingRoomId}/guest_cancellation_confirmations.json?${paramString}`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/booking_rooms/${bookingRoomId}/guest_cancellation_confirmations.json?${paramString}`;
    const result = await createWrapper().get(url);
    return {
      booking: result.data.booking,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const cancelBookingRoomGuests = createAsyncThunk<
  {
    booking: Booking.Booking;
  },
  {
    context: PageContext.PageContext;
    bookingRoomId: string;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/cancelBookingRoomGuests`, async (args, { rejectWithValue }) => {
  try {
    const { context, bookingRoomId, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/booking_rooms/${bookingRoomId}/guest_cancellations.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/booking_rooms/${bookingRoomId}/guest_cancellations.json`;
    const result = await createWrapper().post(url, {
      guest_cancellation: params,
    });
    return {
      booking: result.data.booking,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const cancelBookingOption = createAsyncThunk<
  {
    booking: Booking.Booking;
  },
  {
    context: PageContext.PageContext;
    id: number;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/cancelBookingOption`, async (args, { rejectWithValue }) => {
  try {
    const { context, id, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/booking_options/${id}/cancellations.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/booking_options/${id}/cancellations.json`;
    const result = await createWrapper().post(url, { cancellation: params });
    return {
      booking: result.data.booking,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const putBooking = createAsyncThunk<
  {
    booking: Booking.Booking;
  },
  {
    context: PageContext.PageContext;
    id: string;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/putBooking`, async (args, { rejectWithValue }) => {
  try {
    const { context, id, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/bookings/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/bookings/${id}.json`;
    const result = await createWrapper().put(url, {
      booking: params,
    });
    return {
      booking: result.data.booking,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const postBookingCancellationChargeReduction = createAsyncThunk<
  {
    booking: Booking.Booking;
  },
  {
    context: PageContext.PageContext;
    id: string;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(
  `${key}/postBookingCancellationChargeReduction`,
  async (args, { rejectWithValue }) => {
    try {
      const { context, id, params } = args;
      const url =
        context.adminType === AdminTypes.manager
          ? `${API_BASE_PATH}/manager/bookings/${id}/cancellation_charge_reduction.json`
          : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/bookings/${id}/cancellation_charge_reduction.json`;
      const result = await createWrapper().post(url, {
        cancellation_charge_reduction: params,
      });
      return {
        booking: result.data.booking,
      };
    } catch (err) {
      return rejectWithValue(err.data.errors);
    }
  },
);

export const resendBookingMail = createAsyncThunk<
  void,
  {
    context: PageContext.PageContext;
    id: string;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/resendBookingMail`, async (args, { rejectWithValue }) => {
  try {
    const { context, id, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/bookings/${id}/resend_mails.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/bookings/${id}/resend_mails.json`;
    await createWrapper().post(url, params);
    return;
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const sendCreditCardChangeMail = createAsyncThunk<
  void,
  {
    context: PageContext.PageContext;
    id: string;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/sendCreditCardChangeMail`, async (args, { rejectWithValue }) => {
  try {
    const { context, id, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/bookings/${id}/send_credit_card_change_mails.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/bookings/${id}/send_credit_card_change_mails.json`;
    await createWrapper().post(url, params);
    return;
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

/** slice * */
export const bookingSlice = createSlice({
  name: key,
  initialState,
  reducers: {
    clearSelectedBooking: (state) => {
      state.bookingId = null;
    },
    setBookingParams: (state, action) => {
      state.params = action.payload;
    },
    initSort: (state, action) => {
      state.sortName = action.payload.sortName;
      state.sortOrder = action.payload.sortOrder;
      // initSortByは初期化用のためisSortByUpdatedをfalseにする
      state.isSortUpdated = false;
    },
    setNextSort: (state, action) => {
      if (state.sortName === action.payload) {
        if (state.sortOrder === SortOrderTypes.desc) {
          state.sortOrder = SortOrderTypes.asc;
        } else {
          state.sortName = initialStateValues.sortName;
          state.sortOrder = initialStateValues.sortOrder;
        }
      } else {
        state.sortName = action.payload;
        state.sortOrder = SortOrderTypes.desc;
      }
      state.isSortUpdated = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(HYDRATE, (state, action: any) => {
        return { ...state, ...action.payload.booking };
      })
      .addCase(fetchManagerBookings.fulfilled, (state, action) => {
        managerBookingAdapter.setAll(
          state.managerBookings,
          action.payload.bookings,
        );
        state.managerBookings.meta = action.payload.meta;
      })
      .addCase(fetchBookings.fulfilled, (state, action) => {
        adapter.setAll(state, action.payload.bookings);
        state.meta = action.payload.meta;
      })
      .addCase(fetchBooking.pending, (state, action) => {
        const targetBookingId = action.meta.arg.id;
        if (state.bookingId && state.bookingId !== targetBookingId) {
          // 別の予約データを取得するため選択中の予約をクリアする
          state.bookingId = null;
        }
      })
      .addCase(fetchBooking.fulfilled, (state, action) => {
        adapter.upsertOne(state, action.payload.booking);
        state.bookingId = action.payload.booking.code;
      })
      .addCase(cancelBooking.fulfilled, (state, action) => {
        const { code, ...changes } = action.payload.booking;
        adapter.updateOne(state, { id: code, changes });
      })
      .addCase(confirmBooking.fulfilled, (state, action) => {
        const { code, ...changes } = action.payload.booking;
        adapter.updateOne(state, { id: code, changes });
      })
      .addCase(cancelBookingRoom.fulfilled, (state, action) => {
        const { code, ...changes } = action.payload.booking;
        adapter.updateOne(state, { id: code, changes });
      })
      .addCase(cancelBookingRoomGuests.fulfilled, (state, action) => {
        const { code, ...changes } = action.payload.booking;
        adapter.updateOne(state, { id: code, changes });
      })
      .addCase(cancelBookingOption.fulfilled, (state, action) => {
        const { code, ...changes } = action.payload.booking;
        adapter.updateOne(state, { id: code, changes });
      })
      .addCase(putBooking.fulfilled, (state, action) => {
        const { code, ...changes } = action.payload.booking;
        adapter.updateOne(state, { id: code, changes });
      })
      .addCase(
        postBookingCancellationChargeReduction.fulfilled,
        (state, action) => {
          const { code, ...changes } = action.payload.booking;
          adapter.updateOne(state, { id: code, changes });
        },
      )
      // 一旦slice単位で共通化
      .addMatcher(isPendingAction(key), (state) => {
        state.loading = true;
      })
      .addMatcher(isFulfilledAction(key), (state) => {
        state.loading = false;
      })
      .addMatcher(isRejectedAction(key), (state, action: any) => {
        state.error = action.payload?.error_messages;
        state.loading = false;
      });
  },
});

/** selector **/
const stateSelector = (state: RootState) => state[key];

export const { selectAll: bookingsSelector, selectById: bookingByIdSelector } =
  adapter.getSelectors((state: RootState) => state.booking);
export const {
  selectAll: managerBookingsSelector,
  selectById: managerBookingByIdSelector,
} = managerBookingAdapter.getSelectors(
  (state: RootState) => state.booking.managerBookings,
);

export const bookingSelector = createSelector(
  stateSelector,
  (state) => state.entities[state.bookingId],
);

export const bookingParamsSelector = createSelector(
  stateSelector,
  (state) => state.params,
);

export const loadingSelector = createSelector(
  stateSelector,
  (state) => state.loading,
);

export const bookingsMetaSelector = createSelector(
  stateSelector,
  (state) => state.meta,
);

export const sortNameSelector = createSelector(
  stateSelector,
  (state) => state.sortName,
);

export const sortOrderSelector = createSelector(
  stateSelector,
  (state) => state.sortOrder,
);

export const isSortUpdatedSelector = createSelector(
  stateSelector,
  (state) => state.isSortUpdated,
);

export const managerBookingsMetaSelector = createSelector(
  stateSelector,
  (state) => state.managerBookings.meta,
);

/** action export **/
export const { clearSelectedBooking, setBookingParams, initSort, setNextSort } =
  bookingSlice.actions;

export const getNumberOfGuestsLabel = (isNonStay: boolean, t: any) => {
  return isNonStay
    ? t("adminLabel:booking.numberOfParticipants")
    : t("adminLabel:booking.numberOfGuests");
};

export const getGuestNameLabel = (isNonStay: boolean, t: any) => {
  return isNonStay
    ? t("adminComponent:booking.participantName")
    : t("adminComponent:booking.guestName");
};

export const getDateOfStayLabel = (isNonStay: boolean, t: any) => {
  return isNonStay ? t("label:hotel.dateOfUse") : t("label:hotel.dateOfStay");
};

export const bookingRoomTotalChildren = (bookingRoom: Booking.BookingRoom) => {
  const childrenKeys = Object.keys(HotelChildrenSettingAllCategories).map(
    (key) => {
      return HotelChildrenSettingAllCategories[key];
    },
  );
  return childrenKeys.reduce((acc: number, cur: string) => {
    const children = bookingRoom[camelize(cur)];
    return children ? acc + Number(children) : acc;
  }, 0);
};
